import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Zap, TrendingUp, Users, DollarSign, CheckCircle, Activity,
  ArrowRight, ArrowUpRight, Clock, Sparkles, Orbit, Flame,
  ChevronDown,
} from 'lucide-react';

/* ============================================================
   COSMIC HOME — "Illuminate the Unknown"
   Inspired by theunknown.tv (cinematic, dark, layered).
   Colors locked to user's puzzle-head artwork: deep indigo,
   electric violet, magenta rays, ember orange highlights.
   ============================================================ */

const HERO_IMG = 'https://customer-assets.emergentagent.com/job_emergent-nextjs-llm/artifacts/urwqvqw8_75a7e073-6d51-4695-9485-f56041a889e9.png';

/* ---------- helpers ---------- */
function useInView(threshold = 0.18) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current || visible) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [threshold, visible]);
  return [ref, visible];
}

function AnimatedCounter({ end, duration = 1800, prefix = '', suffix = '', decimals = 0, start: shouldStart = true }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldStart) return;
    const startTs = performance.now();
    let raf;
    const tick = (ts) => {
      const elapsed = ts - startTs;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setCount(end * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, shouldStart]);
  return <span>{prefix}{decimals ? count.toFixed(decimals) : Math.floor(count).toLocaleString()}{suffix}</span>;
}

/* ============================================================
   SECTIONS
   ============================================================ */

function HeroFace({ activeAgents, scrollY }) {
  // Image breathes alone. As user scrolls, it parallax-fades upward, transitioning
  // smoothly into the intro section below.
  const parallax = Math.min(scrollY * 0.45, 260);
  const fade = Math.max(0, 1 - scrollY / 700);

  return (
    <section
      data-testid="home-hero-face"
      style={{
        position: 'relative',
        height: '100vh',
        background: 'radial-gradient(ellipse 90% 70% at 50% 45%, #2A0F5C 0%, #14062E 45%, #0B0420 100%)',
        overflow: 'hidden',
        color: 'var(--cosmic-text)',
      }}
    >
      {/* Cosmic rays — rotating slowly behind the face */}
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: '160vmax', height: '160vmax',
        transform: `translate(-50%, -50%) translateY(${parallax * 0.4}px)`,
        background: `conic-gradient(from 0deg,
          transparent 0deg,
          rgba(255,107,26,0.18) 10deg,
          transparent 30deg,
          rgba(192,38,211,0.16) 60deg,
          transparent 90deg,
          rgba(124,58,237,0.20) 130deg,
          transparent 160deg,
          rgba(255,140,66,0.12) 200deg,
          transparent 230deg,
          rgba(91,46,230,0.18) 260deg,
          transparent 290deg,
          rgba(255,209,102,0.10) 320deg,
          transparent 360deg
        )`,
        animation: 'cosmic-rays-rotate 90s linear infinite',
        opacity: fade * 0.9,
        filter: 'blur(40px)',
        pointerEvents: 'none',
      }} />

      {/* The puzzle head — centered, larger, untouched by text */}
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 'min(640px, 78vmin)',
        height: 'min(640px, 78vmin)',
        transform: `translate(-50%, calc(-50% + ${parallax * 0.18}px))`,
        opacity: fade,
        pointerEvents: 'none',
      }}>
        <img
          src={HERO_IMG}
          alt=""
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
            filter: 'saturate(1.18) contrast(1.06)',
            maskImage: 'radial-gradient(circle, #000 58%, transparent 82%)',
            WebkitMaskImage: 'radial-gradient(circle, #000 58%, transparent 82%)',
          }}
        />
        {/* Ember breathing halo */}
        <div style={{
          position: 'absolute', inset: '-10%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, transparent 52%, rgba(255,107,26,0.30) 68%, transparent 82%)',
          animation: 'cosmic-glow-breathe 5s ease-in-out infinite',
        }} />
        {/* Violet outer aura */}
        <div style={{
          position: 'absolute', inset: '-25%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, transparent 60%, rgba(124,58,237,0.18) 75%, transparent 88%)',
          animation: 'cosmic-glow-breathe 7s ease-in-out infinite reverse',
        }} />
      </div>

      {/* Twinkling particles */}
      {Array.from({ length: 22 }).map((_, i) => (
        <span key={i} style={{
          position: 'absolute',
          left: `${(i * 47) % 100}%`,
          top: `${(i * 31) % 100}%`,
          width: i % 4 === 0 ? 4 : 2,
          height: i % 4 === 0 ? 4 : 2,
          borderRadius: '50%',
          background: i % 3 === 0 ? '#FF6B1A' : i % 3 === 1 ? '#C026D3' : '#FFD166',
          boxShadow: `0 0 10px ${i % 3 === 0 ? '#FF6B1A' : i % 3 === 1 ? '#C026D3' : '#FFD166'}`,
          opacity: fade * 0.75,
          animation: `cosmic-twinkle ${2 + (i % 5) * 0.6}s ease-in-out infinite ${(i % 7) * 0.2}s`,
        }} />
      ))}

      {/* Top eyebrow chip removed — let the face breathe in silence */}

      {/* Soft bottom gradient — bridges into HeroIntro below */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: 160,
        background: 'linear-gradient(180deg, transparent 0%, #0B0420 100%)',
        pointerEvents: 'none',
      }} />

      {/* Scroll hint */}
      <div style={{
        position: 'absolute', bottom: 48, left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        color: 'rgba(244,243,240,0.5)',
        fontSize: 10, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.35em',
        opacity: fade,
        zIndex: 2,
      }}>
        <span>Scroll to enter</span>
        <ChevronDown size={18} style={{ animation: 'cosmic-scroll-hint 1.8s ease-in-out infinite' }} />
      </div>
    </section>
  );
}

function HeroIntro({ user, activeAgents, totalMissions, onNavigate }) {
  const [ref, visible] = useInView(0.15);
  const firstName = (user?.name || '').trim().split(/\s+/)[0] || 'friend';
  const avatarUrl = user?.picture;
  const initial = (firstName[0] || 'U').toUpperCase();

  // First-visit detection: scoped to the user, persisted in localStorage so it
  // survives reloads but a returning user sees "Welcome back" forever after.
  // Lazy init so state is set synchronously without a useEffect+setState.
  const [isFirstVisit] = useState(() => {
    const key = `studio_welcomed_${user?.id || user?.email || 'anon'}`;
    try {
      if (typeof window === 'undefined' || !window.localStorage) return false;
      if (!localStorage.getItem(key)) {
        localStorage.setItem(key, String(Date.now()));
        return true;
      }
    } catch (e) {
      // localStorage blocked — fall back to "Welcome back" silently
    }
    return false;
  });

  const welcomeLabel = isFirstVisit ? 'Welcome' : 'Welcome back';

  return (
    <section
      ref={ref}
      data-testid="home-hero-intro"
      style={{
        position: 'relative',
        background: 'linear-gradient(180deg, #0B0420 0%, #14062E 60%, #0B0420 100%)',
        padding: '140px 6vw 160px',
        color: 'var(--cosmic-text)',
        overflow: 'hidden',
        textAlign: 'center',
      }}
    >
      {/* Faint cosmic echo — keeps the face's spirit without literal repeat */}
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 900, height: 900,
        transform: 'translate(-50%, -50%)',
        background: 'radial-gradient(circle, rgba(124,58,237,0.10) 0%, transparent 60%)',
        pointerEvents: 'none',
        animation: 'cosmic-glow-breathe 6s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 600, height: 600,
        transform: 'translate(-50%, -50%)',
        background: 'radial-gradient(circle, rgba(255,107,26,0.08) 0%, transparent 65%)',
        pointerEvents: 'none',
        animation: 'cosmic-glow-breathe 7.5s ease-in-out infinite reverse',
      }} />

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '0 auto' }}>
        {/* Personal welcome row: "— Welcome back —" + calligraphic name + orbiting avatar */}
        <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'clamp(20px, 3vw, 48px)',
          marginBottom: 56,
        }}>
          {/* Name stack */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 28, height: 1, background: '#FF6B1A' }} />
              <span style={{
                fontSize: 11, fontWeight: 700,
                color: '#FF6B1A',
                textTransform: 'uppercase', letterSpacing: '0.3em',
              }}>
                {welcomeLabel}
              </span>
              <span style={{ width: 28, height: 1, background: '#FF6B1A' }} />
            </div>

            {/* Calligraphic first name — yellow→orange gradient (horizontal flow) */}
            <div
              data-testid="home-firstname"
              style={{
                fontFamily: "'Great Vibes', cursive",
                fontSize: 'clamp(64px, 10vw, 156px)',
                fontWeight: 400,
                lineHeight: 1.18,        // generous so ascenders & descenders breathe
                letterSpacing: '0.005em',
                background: 'linear-gradient(90deg, #FFE89D 0%, #FFC061 45%, #FF9F40 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 30px rgba(255,209,102,0.55)) drop-shadow(0 0 70px rgba(255,159,64,0.30))',
                padding: '0.12em 0.18em 0.28em',  // never let the calligraphy be clipped
                margin: '-0.04em 0',              // tighten the visual block slightly
                overflow: 'visible',
                display: 'inline-block',
                maxWidth: '100%',
                wordBreak: 'normal',
              }}
            >
              {firstName}
            </div>
          </div>

          {/* Orbiting avatar — satellite of the welcome */}
          <div style={{
            position: 'relative',
            width: 'clamp(110px, 12vw, 160px)',
            height: 'clamp(110px, 12vw, 160px)',
            flexShrink: 0,
          }}>
            {/* Outer dashed orbital ring */}
            <div style={{
              position: 'absolute',
              inset: '-22%',
              borderRadius: '50%',
              border: '1px dashed rgba(255,209,102,0.32)',
              animation: 'cosmic-rays-rotate 28s linear infinite',
            }} />
            {/* Inner subtle ring */}
            <div style={{
              position: 'absolute',
              inset: '-8%',
              borderRadius: '50%',
              border: '1px solid rgba(255,209,102,0.18)',
            }} />
            {/* Glow halo */}
            <div style={{
              position: 'absolute',
              inset: '-30%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,209,102,0.28) 0%, transparent 65%)',
              animation: 'cosmic-glow-breathe 4.5s ease-in-out infinite',
              pointerEvents: 'none',
            }} />

            {/* Avatar disc */}
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={firstName}
                referrerPolicy="no-referrer"
                style={{
                  position: 'relative',
                  width: '100%', height: '100%',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(255,232,167,0.55)',
                  boxShadow: '0 12px 40px rgba(255,179,71,0.35), 0 0 0 1px rgba(0,0,0,0.4) inset',
                }}
              />
            ) : (
              <div style={{
                position: 'relative',
                width: '100%', height: '100%',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #FFE89D 0%, #FFB347 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#0B0420', fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 800,
                border: '2px solid rgba(255,232,167,0.55)',
                boxShadow: '0 12px 40px rgba(255,179,71,0.35)',
                fontFamily: 'inherit',
              }}>
                {initial}
              </div>
            )}

            {/* Live status dot — small moon */}
            <span style={{
              position: 'absolute',
              right: '4%', bottom: '4%',
              width: 14, height: 14, borderRadius: '50%',
              background: '#22C55E',
              border: '2px solid #14062E',
              boxShadow: '0 0 12px #22C55E',
              animation: 'pulse-glow 2s ease-in-out infinite',
            }} />
          </div>
        </div>

        {/* Headline */}
        <h1 className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          fontSize: 'clamp(56px, 9vw, 132px)',
          fontWeight: 900,
          letterSpacing: '-0.045em',
          lineHeight: 0.92,
          textTransform: 'uppercase',
          color: 'var(--cosmic-text)',
          margin: 0,
          textShadow: '0 4px 60px rgba(124,58,237,0.4)',
          animationDelay: '0.1s',
        }}>
          Illuminate<br/>
          <span style={{
            background: 'linear-gradient(135deg, #FF6B1A 0%, #C026D3 50%, #7C3AED 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>The Unknown.</span>
        </h1>

        {/* Sub */}
        <p className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          fontSize: 'clamp(15px, 1.4vw, 18px)',
          color: 'rgba(244,243,240,0.62)',
          maxWidth: 620, margin: '36px auto 56px',
          lineHeight: 1.6,
          fontWeight: 400,
          animationDelay: '0.2s',
        }}>
          {totalMissions} missions en orbite. {activeAgents} agents actifs.
          Ton studio respire et compose, en direct.
        </p>

        {/* CTA pill bar */}
        <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          display: 'inline-flex', alignItems: 'center', gap: 0,
          background: 'rgba(244,243,240,0.04)',
          backdropFilter: 'blur(24px)',
          border: '1px solid rgba(244,243,240,0.14)',
          borderRadius: 999,
          padding: 6,
          boxShadow: '0 24px 60px rgba(0,0,0,0.4)',
          animationDelay: '0.3s',
        }}>
          {[
            { label: 'Build', icon: Zap, view: 'build', primary: true },
            { label: 'Agents', icon: Users, view: 'agents' },
            { label: 'Projects', icon: Orbit, view: 'projects' },
            { label: 'Activity', icon: Activity, view: 'activity' },
          ].map(item => (
            <button
              key={item.view}
              data-testid={`home-cta-${item.view}`}
              onClick={() => onNavigate(item.view)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 26px',
                border: 'none',
                borderRadius: 999,
                background: item.primary
                  ? 'linear-gradient(135deg, #FF6B1A 0%, #C026D3 60%, #7C3AED 100%)'
                  : 'transparent',
                color: item.primary ? '#fff' : 'rgba(244,243,240,0.78)',
                fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                transition: 'transform 0.2s, background-color 0.2s, color 0.2s',
                letterSpacing: '0.02em',
                boxShadow: item.primary ? '0 8px 30px rgba(124,58,237,0.45)' : 'none',
              }}
              onMouseEnter={e => {
                if (!item.primary) {
                  e.currentTarget.style.background = 'rgba(244,243,240,0.08)';
                  e.currentTarget.style.color = '#fff';
                }
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                if (!item.primary) {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'rgba(244,243,240,0.78)';
                }
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <item.icon size={15} />
              {item.label}
              {item.primary && <ArrowUpRight size={14} />}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function MarqueeBand() {
  const words = ['ILLUMINATE', 'BUILD', 'COMPOUND', 'MONETIZE', 'TRANSFORM', 'LAUNCH', 'SHIP'];
  const line = words.join('  •  ');
  return (
    <div style={{
      background: '#0B0420',
      borderTop: '1px solid rgba(244,243,240,0.08)',
      borderBottom: '1px solid rgba(244,243,240,0.08)',
      overflow: 'hidden',
      padding: '22px 0',
    }}>
      <div style={{
        display: 'flex', gap: 60,
        animation: 'cosmic-marquee 28s linear infinite',
        whiteSpace: 'nowrap',
        width: 'max-content',
      }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <span key={i} style={{
            fontSize: 'clamp(42px, 6vw, 88px)',
            fontWeight: 900,
            letterSpacing: '-0.03em',
            textTransform: 'uppercase',
            color: 'transparent',
            WebkitTextStroke: '1.5px rgba(244,243,240,0.22)',
          }}>
            {line}
          </span>
        ))}
      </div>
    </div>
  );
}

function KpiAtlas({ stats }) {
  const [ref, visible] = useInView();
  const s = stats || {};
  const kpis = [
    { label: 'Monthly Revenue', value: s.mrr || 0, prefix: '$', icon: DollarSign, color: '#FF6B1A', delta: '+12%', glow: 'rgba(255,107,26,0.4)' },
    { label: 'Missions',        value: s.tasks_total || 0,          icon: CheckCircle, color: '#7C3AED', delta: `${s.tasks_in_progress || 0} active`, glow: 'rgba(124,58,237,0.45)' },
    { label: 'Agents Online',   value: s.agents_active || 0,        icon: Sparkles,    color: '#C026D3', delta: 'all systems go', glow: 'rgba(192,38,211,0.4)' },
    { label: 'Revenue Today',   value: s.revenue_today || 0, prefix: '$', icon: Flame, color: '#FFD166', delta: '+23%', glow: 'rgba(255,209,102,0.4)' },
  ];

  return (
    <section ref={ref} style={{
      position: 'relative',
      background: 'linear-gradient(180deg, #0B0420 0%, #14062E 100%)',
      padding: '120px 6vw',
      color: 'var(--cosmic-text)',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{ marginBottom: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ width: 32, height: 1, background: '#FF6B1A' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#FF6B1A', textTransform: 'uppercase', letterSpacing: '0.3em' }}>
              The Atlas
            </span>
          </div>
          <h2 style={{
            fontSize: 'clamp(40px, 5.5vw, 80px)',
            fontWeight: 900,
            letterSpacing: '-0.04em',
            lineHeight: 0.95,
            textTransform: 'uppercase',
            margin: 0,
          }}>
            What you&apos;ve<br/>
            <span style={{
              background: 'linear-gradient(135deg, #FFD166 0%, #FF6B1A 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              built so far.
            </span>
          </h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          {kpis.map((k, i) => (
            <div
              key={i}
              className={`cosmic-reveal ${visible ? 'is-visible' : ''}`}
              style={{
                position: 'relative',
                padding: 32,
                background: 'rgba(244,243,240,0.03)',
                border: '1px solid rgba(244,243,240,0.10)',
                borderRadius: 24,
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                transition: 'transform 0.3s, border-color 0.3s',
                animationDelay: `${i * 0.12}s`,
                cursor: 'default',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = k.color + '66';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(244,243,240,0.10)';
              }}
            >
              {/* Glow corner */}
              <div style={{
                position: 'absolute',
                top: -60, right: -60,
                width: 180, height: 180,
                background: `radial-gradient(circle, ${k.glow} 0%, transparent 70%)`,
                pointerEvents: 'none',
              }} />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  color: 'rgba(244,243,240,0.45)',
                  textTransform: 'uppercase', letterSpacing: '0.2em',
                }}>{k.label}</span>
                <div style={{
                  width: 36, height: 36, borderRadius: 12,
                  background: k.color + '22',
                  border: `1px solid ${k.color}44`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <k.icon size={16} style={{ color: k.color }} />
                </div>
              </div>

              <div style={{
                fontSize: 56,
                fontWeight: 900,
                color: '#fff',
                letterSpacing: '-0.035em',
                lineHeight: 1,
                marginBottom: 16,
              }}>
                <AnimatedCounter end={k.value} prefix={k.prefix || ''} duration={1800 + i * 250} start={visible} />
              </div>

              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: 11, fontWeight: 700,
                color: k.color,
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                <TrendingUp size={11} />
                {k.delta}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ConstellationSection({ agents, onNavigate }) {
  const [ref, visible] = useInView();
  const list = agents || [];
  const orbits = list.slice(0, 8);

  return (
    <section ref={ref} style={{
      position: 'relative',
      background: 'linear-gradient(180deg, #14062E 0%, #1A0938 100%)',
      padding: '140px 6vw',
      color: 'var(--cosmic-text)',
      overflow: 'hidden',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 80, alignItems: 'center' }}>
        {/* LEFT — copy */}
        <div className={`cosmic-reveal-l cosmic-reveal ${visible ? 'is-visible' : ''}`}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ width: 32, height: 1, background: '#C026D3' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#C026D3', textTransform: 'uppercase', letterSpacing: '0.3em' }}>
              The Constellation
            </span>
          </div>
          <h2 style={{
            fontSize: 'clamp(36px, 4.8vw, 72px)',
            fontWeight: 900,
            letterSpacing: '-0.04em',
            lineHeight: 0.95,
            textTransform: 'uppercase',
            margin: '0 0 28px',
          }}>
            Your team<br/>
            <span style={{
              background: 'linear-gradient(135deg, #C026D3 0%, #7C3AED 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>in orbit.</span>
          </h2>
          <p style={{
            fontSize: 16, lineHeight: 1.7,
            color: 'rgba(244,243,240,0.62)',
            marginBottom: 36,
            maxWidth: 480,
          }}>
            {list.length} agents tournent autour de ton venture. Chacun a son rôle,
            son tempo, sa charge. Survole l&apos;orbite pour les voir respirer.
          </p>

          {/* Mini agent list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {list.slice(0, 4).map((a, i) => (
              <div key={a.agent_id || i} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 18px',
                background: 'rgba(244,243,240,0.03)',
                border: '1px solid rgba(244,243,240,0.08)',
                borderRadius: 14,
                transition: 'border-color 0.2s, transform 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = (a.color || '#7C3AED') + '66'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(244,243,240,0.08)'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  background: (a.color || '#7C3AED') + '33',
                  border: `1px solid ${a.color || '#7C3AED'}66`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 13, fontWeight: 800,
                }}>{a.name?.[0] || 'A'}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{a.name}</div>
                  <div style={{ fontSize: 11, color: 'rgba(244,243,240,0.4)' }}>{a.role}</div>
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: a.color || '#7C3AED',
                  letterSpacing: '0.05em',
                }}>{a.tasks_completed || 0}/{a.tasks_total || 0}</div>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: a.status === 'active' ? '#22C55E' : 'rgba(244,243,240,0.2)',
                  boxShadow: a.status === 'active' ? '0 0 12px #22C55E' : 'none',
                }} />
              </div>
            ))}
            <button
              data-testid="home-agents-view-all"
              onClick={() => onNavigate('agents')}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '14px 24px',
                background: 'rgba(244,243,240,0.04)',
                border: '1px solid rgba(244,243,240,0.16)',
                borderRadius: 999,
                color: '#fff',
                fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
                textTransform: 'uppercase', letterSpacing: '0.15em',
                marginTop: 8, alignSelf: 'flex-start',
                width: 'fit-content',
                transition: 'background-color 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,243,240,0.08)'; e.currentTarget.style.borderColor = 'rgba(244,243,240,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,243,240,0.04)'; e.currentTarget.style.borderColor = 'rgba(244,243,240,0.16)'; }}
            >
              See all agents <ArrowRight size={12} />
            </button>
          </div>
        </div>

        {/* RIGHT — orbit visual */}
        <div className={`cosmic-reveal-r cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          position: 'relative',
          aspectRatio: '1 / 1',
          maxWidth: 520,
          marginLeft: 'auto',
          width: '100%',
        }}>
          {/* Orbital rings */}
          {[140, 200, 260].map((r, i) => (
            <div key={r} style={{
              position: 'absolute',
              top: '50%', left: '50%',
              width: r * 2, height: r * 2,
              transform: 'translate(-50%, -50%)',
              borderRadius: '50%',
              border: '1px dashed rgba(244,243,240,0.12)',
              animation: `cosmic-rays-rotate ${80 - i * 20}s linear infinite ${i % 2 ? 'reverse' : ''}`,
            }} />
          ))}

          {/* Core — small image echo */}
          <div style={{
            position: 'absolute',
            top: '50%', left: '50%',
            width: 120, height: 120,
            transform: 'translate(-50%, -50%)',
            borderRadius: '50%',
            background: `url(${HERO_IMG}) center/cover`,
            boxShadow: '0 0 60px rgba(124,58,237,0.6), 0 0 120px rgba(255,107,26,0.3)',
            border: '2px solid rgba(244,243,240,0.2)',
          }} />

          {/* Orbiting agents */}
          {orbits.map((a, i) => {
            const radii = [140, 200, 260];
            const r = radii[i % 3];
            const angle = (i / Math.max(orbits.length, 1)) * Math.PI * 2;
            const x = Math.cos(angle) * r;
            const y = Math.sin(angle) * r;
            return (
              <div
                key={a.agent_id || i}
                style={{
                  position: 'absolute',
                  top: '50%', left: '50%',
                  transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                  zIndex: 2,
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: (a.color || '#7C3AED'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff', fontSize: 14, fontWeight: 800,
                  boxShadow: `0 0 20px ${a.color || '#7C3AED'}`,
                  border: '2px solid rgba(244,243,240,0.18)',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  title={a.name}
                >
                  {a.name?.[0] || 'A'}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function MissionsSection({ projects, onNavigate }) {
  const [ref, visible] = useInView();
  const list = (projects || []).slice(0, 6);

  return (
    <section ref={ref} style={{
      position: 'relative',
      background: 'linear-gradient(180deg, #1A0938 0%, #14062E 100%)',
      padding: '140px 6vw',
      color: 'var(--cosmic-text)',
    }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          marginBottom: 64, flexWrap: 'wrap', gap: 24,
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ width: 32, height: 1, background: '#7C3AED' }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: '0.3em' }}>
                In Flight
              </span>
            </div>
            <h2 style={{
              fontSize: 'clamp(36px, 4.8vw, 72px)',
              fontWeight: 900,
              letterSpacing: '-0.04em',
              lineHeight: 0.95,
              textTransform: 'uppercase',
              margin: 0,
            }}>
              Missions<br/>
              <span style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #FF6B1A 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>en orbite.</span>
            </h2>
          </div>
          <button
            data-testid="home-projects-view-all"
            onClick={() => onNavigate('projects')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 24px',
              background: 'transparent',
              border: '1px solid rgba(244,243,240,0.18)',
              borderRadius: 999,
              color: '#fff',
              fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
              textTransform: 'uppercase', letterSpacing: '0.15em',
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,243,240,0.06)'; e.currentTarget.style.borderColor = 'rgba(244,243,240,0.35)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(244,243,240,0.18)'; }}
          >
            All projects <ArrowRight size={12} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
          {list.length === 0 && (
            <div style={{
              gridColumn: '1 / -1',
              padding: '60px 28px',
              background: 'rgba(244,243,240,0.03)',
              border: '1px dashed rgba(244,243,240,0.16)',
              borderRadius: 24,
              textAlign: 'center',
              color: 'rgba(244,243,240,0.45)',
              fontSize: 13,
            }}>
              No mission in flight yet. <button onClick={() => onNavigate('build')} style={{ background: 'none', border: 'none', color: '#FF6B1A', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Start one →</button>
            </div>
          )}
          {list.map((p, i) => (
            <div
              key={p.project_id || i}
              className={`cosmic-reveal ${visible ? 'is-visible' : ''}`}
              style={{
                position: 'relative',
                padding: 28,
                background: 'rgba(244,243,240,0.03)',
                border: '1px solid rgba(244,243,240,0.10)',
                borderRadius: 20,
                overflow: 'hidden',
                animationDelay: `${i * 0.1}s`,
                cursor: 'pointer',
                transition: 'transform 0.3s, border-color 0.3s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.borderColor = 'rgba(124,58,237,0.45)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'rgba(244,243,240,0.10)';
              }}
              onClick={() => onNavigate('projects')}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700,
                  padding: '4px 10px',
                  background: p.status === 'active' ? 'rgba(124,58,237,0.18)' : 'rgba(244,243,240,0.06)',
                  color: p.status === 'active' ? '#A78BFA' : 'rgba(244,243,240,0.45)',
                  borderRadius: 999,
                  textTransform: 'uppercase', letterSpacing: '0.15em',
                }}>
                  {p.status || 'idle'}
                </span>
                <ArrowUpRight size={16} style={{ color: 'rgba(244,243,240,0.3)' }} />
              </div>

              <div style={{
                fontSize: 20, fontWeight: 700,
                color: '#fff', letterSpacing: '-0.02em',
                marginBottom: 28,
                lineHeight: 1.2,
                minHeight: 48,
              }}>{p.name}</div>

              {/* Progress horizon */}
              <div style={{ marginBottom: 8 }}>
                <div style={{
                  position: 'relative',
                  height: 6,
                  background: 'rgba(244,243,240,0.06)',
                  borderRadius: 999,
                  overflow: 'hidden',
                }}>
                  <div style={{
                    position: 'absolute', inset: 0,
                    width: `${p.progress || 0}%`,
                    background: 'linear-gradient(90deg, #7C3AED 0%, #FF6B1A 100%)',
                    boxShadow: '0 0 16px rgba(124,58,237,0.5)',
                    transition: 'width 1.2s ease',
                  }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(244,243,240,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                <span>Progress</span>
                <span style={{ color: '#FF6B1A', fontWeight: 700 }}>{p.progress || 0}%</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PulseSection({ activityFeed, onNavigate }) {
  const [ref, visible] = useInView();
  const feed = (activityFeed || []).slice(0, 8);

  return (
    <section ref={ref} style={{
      position: 'relative',
      background: 'linear-gradient(180deg, #14062E 0%, #0B0420 100%)',
      padding: '140px 6vw',
      color: 'var(--cosmic-text)',
    }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{ marginBottom: 56, textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ width: 32, height: 1, background: '#FF6B1A' }} />
            <span style={{ fontSize: 11, fontWeight: 700, color: '#FF6B1A', textTransform: 'uppercase', letterSpacing: '0.3em' }}>
              The Pulse
            </span>
            <span style={{ width: 32, height: 1, background: '#FF6B1A' }} />
          </div>
          <h2 style={{
            fontSize: 'clamp(36px, 4.8vw, 72px)',
            fontWeight: 900,
            letterSpacing: '-0.04em',
            lineHeight: 0.95,
            textTransform: 'uppercase',
            margin: 0,
          }}>
            Right now,<br/>
            <span style={{
              background: 'linear-gradient(135deg, #FF6B1A 0%, #FFD166 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>your studio is alive.</span>
          </h2>
        </div>

        {/* Vertical timeline */}
        <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{
          position: 'relative',
          padding: '24px 0 24px 32px',
          maxWidth: 720,
          margin: '0 auto',
        }}>
          {/* Spine */}
          <div style={{
            position: 'absolute',
            top: 24, bottom: 24, left: 11,
            width: 1,
            background: 'linear-gradient(180deg, transparent 0%, rgba(124,58,237,0.4) 15%, rgba(124,58,237,0.4) 85%, transparent 100%)',
          }} />

          {feed.length === 0 && (
            <div style={{
              padding: '40px 28px',
              background: 'rgba(244,243,240,0.03)',
              border: '1px dashed rgba(244,243,240,0.14)',
              borderRadius: 18,
              color: 'rgba(244,243,240,0.4)',
              textAlign: 'center', fontSize: 13,
            }}>
              No pulse yet — let your agents work.
            </div>
          )}

          {feed.map((ev, i) => (
            <div key={i} style={{
              position: 'relative',
              padding: '14px 0 14px 28px',
              animation: visible ? `cosmic-reveal-up 0.7s cubic-bezier(0.22,1,0.36,1) ${i * 0.08}s both` : 'none',
            }}>
              {/* Dot */}
              <div style={{
                position: 'absolute',
                left: -22, top: 18,
                width: 12, height: 12, borderRadius: '50%',
                background: ev.color || '#7C3AED',
                boxShadow: `0 0 14px ${ev.color || '#7C3AED'}`,
                border: '2px solid #0B0420',
              }} />

              <div style={{
                padding: '16px 20px',
                background: 'rgba(244,243,240,0.03)',
                border: '1px solid rgba(244,243,240,0.08)',
                borderRadius: 14,
                transition: 'border-color 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = (ev.color || '#7C3AED') + '55'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(244,243,240,0.08)'}
              >
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 10, fontWeight: 700,
                  color: ev.color || '#A78BFA',
                  textTransform: 'uppercase', letterSpacing: '0.15em',
                  marginBottom: 6,
                }}>
                  <span>{ev.agent || 'Studio'}</span>
                  <span style={{ color: 'rgba(244,243,240,0.25)' }}>·</span>
                  <Clock size={9} style={{ color: 'rgba(244,243,240,0.3)' }} />
                  <span style={{ color: 'rgba(244,243,240,0.4)' }}>{ev.time_ago || 'just now'}</span>
                </div>
                <div style={{ fontSize: 14, color: 'rgba(244,243,240,0.85)', lineHeight: 1.5 }}>
                  {ev.message}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 56 }}>
          <button
            data-testid="home-activity-view-all"
            onClick={() => onNavigate('activity')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px',
              background: 'rgba(244,243,240,0.04)',
              border: '1px solid rgba(244,243,240,0.18)',
              borderRadius: 999,
              color: '#fff',
              fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'inherit',
              textTransform: 'uppercase', letterSpacing: '0.15em',
              transition: 'background-color 0.2s, border-color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(244,243,240,0.08)'; e.currentTarget.style.borderColor = 'rgba(244,243,240,0.32)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(244,243,240,0.04)'; e.currentTarget.style.borderColor = 'rgba(244,243,240,0.18)'; }}
          >
            Open the full feed <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </section>
  );
}

function FinalCta({ onNavigate }) {
  const [ref, visible] = useInView();
  return (
    <section ref={ref} style={{
      position: 'relative',
      background: 'radial-gradient(ellipse 70% 80% at 50% 40%, #2A0F5C 0%, #0B0420 70%)',
      padding: '160px 6vw 200px',
      textAlign: 'center',
      color: 'var(--cosmic-text)',
      overflow: 'hidden',
    }}>
      {/* Halo */}
      <div style={{
        position: 'absolute',
        top: '40%', left: '50%',
        width: 600, height: 600,
        transform: 'translate(-50%, -50%)',
        background: 'radial-gradient(circle, rgba(255,107,26,0.18) 0%, transparent 60%)',
        pointerEvents: 'none',
        animation: 'cosmic-glow-breathe 6s ease-in-out infinite',
      }} />

      <div className={`cosmic-reveal ${visible ? 'is-visible' : ''}`} style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{
          fontSize: 'clamp(48px, 7vw, 110px)',
          fontWeight: 900,
          letterSpacing: '-0.045em',
          lineHeight: 0.92,
          textTransform: 'uppercase',
          margin: '0 0 32px',
        }}>
          Ready to<br/>
          <span style={{
            background: 'linear-gradient(135deg, #FF6B1A 0%, #C026D3 50%, #7C3AED 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>ship the unknown?</span>
        </h2>
        <p style={{
          fontSize: 16, color: 'rgba(244,243,240,0.6)',
          maxWidth: 520, margin: '0 auto 48px',
          lineHeight: 1.6,
        }}>
          Une idée. Un prompt. Ton équipe d&apos;agents prend la suite.
          Build, monetize, scale — en une seule respiration.
        </p>
        <button
          data-testid="home-cta-launch"
          onClick={() => onNavigate('build')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '20px 40px',
            background: 'linear-gradient(135deg, #FF6B1A 0%, #C026D3 60%, #7C3AED 100%)',
            border: 'none',
            borderRadius: 999,
            color: '#fff',
            fontSize: 14, fontWeight: 800,
            cursor: 'pointer', fontFamily: 'inherit',
            textTransform: 'uppercase', letterSpacing: '0.15em',
            boxShadow: '0 20px 60px rgba(124,58,237,0.5), 0 0 0 1px rgba(244,243,240,0.1) inset',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
            e.currentTarget.style.boxShadow = '0 28px 80px rgba(124,58,237,0.65), 0 0 0 1px rgba(244,243,240,0.18) inset';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = '0 20px 60px rgba(124,58,237,0.5), 0 0 0 1px rgba(244,243,240,0.1) inset';
          }}
        >
          <Zap size={16} /> Launch a build <ArrowUpRight size={16} />
        </button>
      </div>
    </section>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export default function DashboardHome({ user, stats, agents, projects, activityFeed, onNavigate }) {
  const scrollContainerRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const onScroll = () => setScrollY(el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const s = stats || {};
  const activeAgents = useMemo(() => (agents || []).filter(a => a.status === 'active').length, [agents]);
  const totalMissions = s.tasks_total || (projects || []).length;
  const [liveSeconds, setLiveSeconds] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setLiveSeconds(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      ref={scrollContainerRef}
      data-testid="dashboard-home"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#0B0420',
        scrollBehavior: 'smooth',
      }}
    >
      <HeroFace
        activeAgents={activeAgents}
        scrollY={scrollY}
      />
      <HeroIntro
        user={user}
        activeAgents={activeAgents}
        totalMissions={totalMissions}
        onNavigate={onNavigate}
      />
      <MarqueeBand />
      <KpiAtlas stats={stats} />
      <ConstellationSection agents={agents} onNavigate={onNavigate} />
      <MissionsSection projects={projects} onNavigate={onNavigate} />
      <PulseSection activityFeed={activityFeed} onNavigate={onNavigate} />
      <FinalCta onNavigate={onNavigate} />
    </div>
  );
}
